import { useMemo, useState } from 'react';
import { useStore } from '../store';
import type { ExcludedReason } from '../types';

// 除外理由の表示ラベル
const REASON_LABELS: Record<ExcludedReason, string> = {
  robots_txt: 'robots.txt で拒否',
  max_pages: '上限ページ数を超過',
  max_depth: '深度上限を超過',
  invalid_url: '不正な URL',
  non_http: 'http(s) 以外のスキーム',
};

// 理由ごとの色
const REASON_COLORS: Record<ExcludedReason, string> = {
  robots_txt: 'bg-amber-100 text-amber-800',
  max_pages: 'bg-blue-100 text-blue-800',
  max_depth: 'bg-blue-100 text-blue-800',
  invalid_url: 'bg-red-100 text-red-800',
  non_http: 'bg-slate-100 text-slate-800',
};

// 理由の説明（フィルタ UI のホバー説明用）
const REASON_DESCRIPTIONS: Record<ExcludedReason, string> = {
  robots_txt: 'robots.txt の Disallow に該当したため取得しなかった URL',
  max_pages: '--max-pages の上限に達したため取得しなかった URL',
  max_depth: '--depth の上限を超えるため辿らなかった URL',
  invalid_url: 'URL の形式が不正で取得できなかった',
  non_http: 'http(s) 以外のスキーム（mailto: など）',
};

type ReasonFilter = 'all' | ExcludedReason;

export function ExcludedView() {
  const data = useStore((s) => s.data);
  const [reasonFilter, setReasonFilter] = useState<ReasonFilter>('all');
  const [search, setSearch] = useState('');

  // excluded が一切記録されていないかチェック（旧 JSON 対策）
  const hasExcluded = data?.excluded !== undefined;

  // 理由別カウント
  const counts = useMemo(() => {
    const result: Record<ExcludedReason, number> = {
      robots_txt: 0,
      max_pages: 0,
      max_depth: 0,
      invalid_url: 0,
      non_http: 0,
    };
    if (!data?.excluded) return result;
    for (const e of data.excluded) {
      result[e.reason] = (result[e.reason] ?? 0) + 1;
    }
    return result;
  }, [data]);

  // フィルタ後のリスト
  const filtered = useMemo(() => {
    if (!data?.excluded) return [];
    const s = search.trim().toLowerCase();
    return data.excluded.filter((e) => {
      if (reasonFilter !== 'all' && e.reason !== reasonFilter) return false;
      if (s && !e.url.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [data, reasonFilter, search]);

  if (!data) return <></>;

  if (!hasExcluded) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm text-slate-700">
          除外された URL の証跡が記録されていません
        </p>
        <p className="mt-2 text-xs text-slate-500">
          以下の理由が考えられます:
        </p>
        <ul className="mt-1 inline-block text-left text-xs text-slate-500">
          <li>・ 全ページが取得対象内で、除外された URL がそもそもなかった</li>
          <li>・ 古いバージョン (v0.1.x) の JSON で除外証跡機能が未実装だった</li>
        </ul>
      </div>
    );
  }

  const total = data.excluded?.length ?? 0;

  if (total === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-800">
        ✅ 除外された URL はありません（全ページがクロール対象内に収まりました）
      </div>
    );
  }

  // max_pages が支配的なケースでは「次にどう叩けば全件取れるか」を提案する
  const seedUrl = data.crawl.seedUrl;
  const currentMax = data.crawl.options.maxPages;
  const suggestMax = Math.max(currentMax * 2, currentMax + counts.max_pages);
  const showMaxPagesHint = counts.max_pages > 0;

  return (
    <div className="space-y-4">
      {/* このタブが何の一覧かの説明。初見でも一目で分かるようにする */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        <div className="font-semibold text-slate-900">除外された URL とは</div>
        <p className="mt-1 text-xs text-slate-600">
          クロール中に「リンクとして見つけた」けど「実際には取得しなかった」 URL
          の証跡です。除外理由は以下の 5 種類:
        </p>
        <ul className="mt-2 grid gap-1 text-xs text-slate-600 sm:grid-cols-2">
          <li>
            ・<strong>robots.txt で拒否</strong> — robots.txt の Disallow に該当
          </li>
          <li>
            ・<strong>上限ページ数を超過</strong> — <code>--max-pages</code> の上限到達
          </li>
          <li>
            ・<strong>深度上限を超過</strong> — <code>--depth</code> 階層を超えた
          </li>
          <li>
            ・<strong>不正な URL / http(s) 以外</strong> — そもそも辿れない（mailto: など）
          </li>
        </ul>
        {showMaxPagesHint && (
          <div className="mt-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            <div className="font-semibold">全件取得するには</div>
            <p className="mt-0.5">
              現在の <code>--max-pages {currentMax}</code> で {counts.max_pages} 件が打ち切られています。次のコマンドで再クロールしてください:
            </p>
            <pre className="mt-1 overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[11px] text-blue-800">
              {`npx 4ge-sitemap ${seedUrl} --max-pages ${suggestMax}`}
            </pre>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-sm text-slate-700">
          除外された URL: <span className="font-semibold">{total}</span> 件
        </div>
        <input
          type="search"
          placeholder="URL でフィルタ"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-56 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* 理由フィルタ（ボタン群） */}
      <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-4">
        <ReasonButton
          active={reasonFilter === 'all'}
          onClick={() => setReasonFilter('all')}
          label={`すべて (${total})`}
        />
        {(Object.keys(REASON_LABELS) as ExcludedReason[]).map((reason) => {
          const count = counts[reason] ?? 0;
          if (count === 0) return null;
          return (
            <ReasonButton
              key={reason}
              active={reasonFilter === reason}
              onClick={() => setReasonFilter(reason)}
              label={`${REASON_LABELS[reason]} (${count})`}
              title={REASON_DESCRIPTIONS[reason]}
            />
          );
        })}
      </div>

      {/* 除外 URL リスト */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          該当する URL はありません
        </div>
      ) : (
        <ul className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {filtered.map((e, idx) => (
            <li
              key={`${e.url}-${idx}`}
              className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-2 text-xs last:border-0 hover:bg-slate-50"
            >
              <span className="truncate font-mono text-slate-700">{e.url}</span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${REASON_COLORS[e.reason]}`}
                  title={REASON_DESCRIPTIONS[e.reason]}
                >
                  {REASON_LABELS[e.reason]}
                </span>
                {e.detail && (
                  <span className="text-slate-500" title="補足情報">
                    {e.detail}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReasonButton({
  active,
  onClick,
  label,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`rounded-full px-3 py-1 text-xs font-medium transition ${
        active
          ? 'bg-blue-600 text-white'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
    >
      {label}
    </button>
  );
}
